import React, { useEffect, useState, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, BACKEND_URL } from "@/lib/api";
import { Send, Search, MessageSquare, Circle, CheckCircle2, Loader2, X, ChevronDown } from "lucide-react";
import { ClearArea } from "@/components/admin/Purge";
import { toast } from "sonner";

/**
 * /admin/support — Connexis Support inbox.
 *
 * Left rail = thread list with unread badges + presence + status filter.
 * Right pane = active conversation + reply composer.
 * WebSocket pushes any new message in any thread.
 */
export default function AdminSupport() {
  const [threads, setThreads] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("awaiting_admin");
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const listScrollRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTries = useRef(0);

  const scrollMessages = () => {
    requestAnimationFrame(() => {
      if (listScrollRef.current) listScrollRef.current.scrollTop = listScrollRef.current.scrollHeight;
    });
  };

  // ── Load thread list ────────────────────────────────────────────────────
  const loadThreads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter && filter !== "all") params.set("status", filter);
      if (search.trim()) params.set("search", search.trim());
      const r = await api.get(`/admin/support/threads?${params}`);
      setThreads(r.data?.threads || []);
      setUnreadTotal(r.data?.unread_total || 0);
    } catch (e) {
      console.warn("[admin-support] load threads failed", e);
    } finally {
      setLoadingList(false);
    }
  }, [filter, search]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // ── Load active thread + messages ───────────────────────────────────────
  const openThread = async (id) => {
    setActiveId(id);
    setLoadingThread(true);
    try {
      const r = await api.get(`/admin/support/threads/${id}`);
      setActiveThread(r.data?.thread || null);
      setMessages(r.data?.messages || []);
      await api.post(`/admin/support/threads/${id}/read`).catch(() => {});
      // Reflect read state locally
      setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, unread_admin: 0 } : t)));
      setUnreadTotal((u) => Math.max(0, u - (threads.find((t) => t.id === id)?.unread_admin || 0)));
      scrollMessages();
    } finally {
      setLoadingThread(false);
    }
  };

  // ── WebSocket ───────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const connect = () => {
      const token = localStorage.getItem("connexis_token");
      if (!token) return;
      const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/support?token=${encodeURIComponent(token)}`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { reconnectTries.current = 0; };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type !== "support.new_message") return;
            const m = data.message;
            // If it's the active thread, append + auto-read if visible
            setActiveId((curActive) => {
              if (curActive === data.thread_id) {
                setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
                scrollMessages();
                if (m.from_role === "user") {
                  api.post(`/admin/support/threads/${curActive}/read`).catch(() => {});
                }
              }
              return curActive;
            });
            // Update threads list state (preview + unread + reorder)
            setThreads((prev) => {
              const without = prev.filter((t) => t.id !== data.thread_id);
              const existing = prev.find((t) => t.id === data.thread_id);
              if (existing) {
                const updated = {
                  ...existing,
                  last_message_at: m.created_at,
                  last_message_preview: (m.body || "").slice(0, 90),
                  last_message_from: m.from_role,
                  status: m.from_role === "user" ? "awaiting_admin" : "awaiting_user",
                  unread_admin: existing.id === activeId ? 0 : (existing.unread_admin || 0) + (m.from_role === "user" ? 1 : 0),
                };
                return [updated, ...without];
              }
              // New thread we haven't seen yet → refresh list
              loadThreads();
              return prev;
            });
            if (m.from_role === "user") setUnreadTotal((u) => u + 1);
          } catch { /* malformed payload */ }
        };
        ws.onclose = () => {
          if (mounted) {
            const delay = Math.min(30_000, 1_500 * Math.pow(2, reconnectTries.current++));
            setTimeout(connect, delay);
          }
        };
        const hb = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
          else clearInterval(hb);
        }, 45_000);
      } catch (e) { console.warn("[admin-support] ws connect failed", e); }
    };
    connect();
    return () => { mounted = false; try { wsRef.current?.close(); } catch { /* ignore */ } };
  // eslint-disable-next-line
  }, []);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    setDraft("");
    try {
      const r = await api.post(`/admin/support/threads/${activeId}/messages`, { body });
      setMessages((prev) => (prev.some((m) => m.id === r.data.id) ? prev : [...prev, r.data]));
      scrollMessages();
    } catch (err) {
      setDraft(body);
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  const closeThread = async () => {
    if (!activeId) return;
    if (!window.confirm("Mark this conversation as closed?")) return;
    await api.post(`/admin/support/threads/${activeId}/status`, { status: "closed" });
    toast.success("Conversation closed");
    setThreads((prev) => prev.map((t) => (t.id === activeId ? { ...t, status: "closed" } : t)));
    setActiveThread((t) => (t ? { ...t, status: "closed" } : t));
  };

  const filters = [
    { key: "awaiting_admin", label: "Waiting on you" },
    { key: "awaiting_user",  label: "Awaiting reply" },
    { key: "closed",         label: "Closed" },
    { key: "all",            label: "All" },
  ];

  return (
    <div className="h-[calc(100vh-7rem)] grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-0 rounded-xl border border-slate-200 bg-white overflow-hidden shadow-soft" data-testid="admin-support">
      {/* ─── LEFT RAIL: thread list ────────────────────────────────────── */}
      <aside className="border-r border-slate-100 flex flex-col bg-slate-50/30 min-h-0">
        <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-[15px] font-semibold text-slate-900">Inbox</div>
            <div className="text-[11px] text-slate-500">{unreadTotal > 0 ? `${unreadTotal} unread` : "All caught up"}</div>
          </div>
          <ClearArea area="support" onDone={loadThreads} label="Clear" />
        </div>
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="size-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company / email" className="pl-8 text-[12.5px] h-9" data-testid="support-search" />
          </div>
        </div>
        <div className="px-3 flex items-center gap-1.5 flex-wrap pb-2">
          {filters.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
              data-testid={`support-filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="support-thread-list">
          {loadingList && (<div className="px-4 py-8 text-center text-[12px] text-slate-500"><Loader2 className="size-4 animate-spin inline mr-1" />Loading…</div>)}
          {!loadingList && threads.length === 0 && (
            <div className="px-6 py-12 text-center text-[12.5px] text-slate-500">
              <MessageSquare className="size-5 mx-auto mb-2 text-slate-300" />
              No conversations match this filter.
            </div>
          )}
          {threads.map((t) => (
            <button
              key={t.id}
              onClick={() => openThread(t.id)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-white transition-colors ${activeId === t.id ? "bg-white" : ""}`}
              data-testid={`support-thread-${t.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`size-1.5 rounded-full ${t.online ? "bg-emerald-500" : "bg-slate-300"}`} title={t.online ? "Online" : "Offline"} />
                    <div className="text-[13px] font-medium text-slate-900 truncate">{t.user_company || t.user_name || t.user_email}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{t.user_role} · {t.user_email}</div>
                  <div className="text-[12px] text-slate-600 mt-1 truncate">
                    {t.last_message_from === "admin" && <span className="text-slate-400">You: </span>}
                    {t.last_message_preview || <span className="text-slate-400 italic">No messages yet</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {t.last_message_at && <div className="text-[10px] text-slate-400">{relativeTime(t.last_message_at)}</div>}
                  {t.unread_admin > 0 && (
                    <span className="inline-flex items-center justify-center mt-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold" data-testid={`support-unread-${t.id}`}>{t.unread_admin}</span>
                  )}
                  {t.status === "closed" && <CheckCircle2 className="size-3 text-emerald-500 inline-block mt-1" title="Closed" />}
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* ─── RIGHT PANE: active conversation ────────────────────────────── */}
      <section className="flex flex-col min-h-0">
        {!activeId && (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div className="max-w-sm">
              <MessageSquare className="size-9 mx-auto text-slate-300 mb-3" strokeWidth={1.4} />
              <div className="text-[15px] font-medium text-slate-900">Select a conversation</div>
              <div className="text-[12.5px] text-slate-500 mt-1">Pick a thread from the left to reply in real time.</div>
            </div>
          </div>
        )}
        {activeId && (
          <>
            <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[14px] font-semibold text-slate-900 truncate">{activeThread?.user_company || activeThread?.user_name || activeThread?.user_email}</div>
                  {activeThread?.online && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">● Live</span>}
                  {activeThread?.status === "closed" && <span className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5">Closed</span>}
                </div>
                <div className="text-[11.5px] text-slate-500">{activeThread?.user_role} · {activeThread?.user_email}</div>
              </div>
              {activeThread?.status !== "closed" && (
                <Button onClick={closeThread} size="sm" variant="outline" className="rounded-full h-8 text-[12px]" data-testid="support-close-thread"><X className="size-3 mr-1" /> Close conversation</Button>
              )}
            </header>
            <div ref={listScrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-3 bg-slate-50/30" data-testid="support-active-messages">
              {loadingThread && <div className="text-center text-[12px] text-slate-500"><Loader2 className="size-4 animate-spin inline mr-1" />Loading…</div>}
              {!loadingThread && messages.length === 0 && (
                <div className="text-center py-12 text-[12.5px] text-slate-500">No messages yet — say hi to break the ice.</div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.from_role === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[68%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-snug whitespace-pre-wrap break-words ${m.from_role === "admin" ? "bg-[#0055FF] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                    <div className={`text-[10.5px] uppercase tracking-widest font-semibold mb-0.5 ${m.from_role === "admin" ? "text-white/70" : "text-[#0055FF]"}`}>{m.from_role === "admin" ? "You" : (m.from_name || "Customer")}</div>
                    <div>{m.body}</div>
                    <div className={`text-[10px] mt-1 ${m.from_role === "admin" ? "text-white/60" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="border-t border-slate-100 p-3 flex items-end gap-2 bg-white">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                disabled={activeThread?.status === "closed"}
                placeholder={activeThread?.status === "closed" ? "Conversation closed" : "Reply…"}
                rows={2}
                className="flex-1 resize-none outline-none text-[13.5px] px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/15 disabled:bg-slate-50 disabled:text-slate-400"
                data-testid="support-reply-input"
              />
              <Button type="submit" disabled={!draft.trim() || sending || activeThread?.status === "closed"} className="h-10 px-4 rounded-lg" data-testid="support-reply-send">
                {sending ? <Loader2 className="size-4 animate-spin" /> : <><Send className="size-4 mr-1.5" />Send</>}
              </Button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
